import React, { useState, useEffect } from "react";
import authFetch, { setAuthToken } from "../authFetch";
import PT from "../i18n/platformTranslations";
import { anchorDerivedColors } from "../utils/color";
import { buildDarkAccentCss, buildLightAccentCss } from "../utils/themeAccent";
import { useValidationToast, validateFields } from "../utils/validation";
import { usePlatformThemeMode, tc, themeColors } from "./themeUtils";
import CommandNav from "./CommandNav";

function validatePasswordClient(pw){
  if(!pw||pw.length<8)return"Password must be at least 8 characters.";
  if(!/[a-z]/.test(pw))return"Password must include a lowercase letter.";
  if(!/[A-Z]/.test(pw))return"Password must include an uppercase letter.";
  if(!/[0-9]/.test(pw))return"Password must include a number.";
  return null;
}

export default function WorkspaceLogin({platform, workspaces, onLogin, onBack, lang, setLang}){
  const [email,setEmail]=useState("");
  const [pw,setPw]=useState("");
  const [err,setErr]=useState("");
  const [showForgot,setShowForgot]=useState(false);
  const [forgotSent,setForgotSent]=useState(false);
  const [fpCode,setFpCode]=useState("");
  const [fpNewPw,setFpNewPw]=useState("");
  const [fpConfirmPw,setFpConfirmPw]=useState("");
  const [fpShowPw,setFpShowPw]=useState(false);
  const [showLoginPw,setShowLoginPw]=useState(false);
  const [fpSending,setFpSending]=useState(false);
  const [fpStep,setFpStep]=useState(null); /* null→"code"→"newpw"→"done" */
  const [fpErr,setFpErr]=useState("");
  const t=PT[lang];
  const isRTL=lang==="ar";
  const vt=useValidationToast();
  /* Accent default aligned with the landing (violet primary from the
     iridescent gradient system). Admin override still wins. */
  const accent=platform.platformAccent||"#7c3aed";
  const theme=usePlatformThemeMode(platform);
  const C=themeColors(theme);

  const [submitting,setSubmitting]=useState(false);
  /* ── Mobile detection + palette ── */
  const [isMobileLogin,setIsMobileLogin]=useState(()=>typeof window!=="undefined"&&window.innerWidth<=768);
  useEffect(()=>{const h=()=>setIsMobileLogin(window.innerWidth<=768);window.addEventListener("resize",h);return()=>window.removeEventListener("resize",h);},[]);
  const mlAcc=platform.wsDefaultAnchorColor||accent;
  const {primary:mlP,palette:mlPal}=anchorDerivedColors(mlAcc);
  const mlat=(o)=>mlAcc+Math.round(o*255).toString(16).padStart(2,"0");
  const mlInk=theme==="dark"?mlPal[10]:mlP;
  const mlMuted=theme==="dark"?mlPal[6]:mlPal[5];
  const mlSubtle=theme==="dark"?mlPal[4]:mlPal[7];
  const mlGrad=`linear-gradient(135deg,${theme==="dark"?mlPal[5]:mlPal[3]},${theme==="dark"?mlPal[4]:mlPal[2]})`;
  const mlBtnTxt="#fff";
  const mlFont=isRTL?"'Tajawal',-apple-system,sans-serif":"-apple-system,'SF Pro Display','SF Pro Text','Helvetica Neue',sans-serif";
  const mlAccentCss=theme==="dark"?buildDarkAccentCss(mlAcc):buildLightAccentCss(mlAcc);

  const submit=async()=>{
    const m=validateFields([{ok:email.trim(),label:isRTL?"البريد":"Email"},{ok:pw,label:isRTL?"كلمة المرور":"Password"}]);
    if(m.length){vt.show(m.join(", "));setErr(isRTL?"أدخل البريد وكلمة المرور":"Enter email and password.");return;}
    setSubmitting(true);setErr("");
    try{
      const r=await authFetch("/api/auth",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({action:"login_platform",email:email.trim(),password:pw})});
      const d=await r.json();
      if(!r.ok){
        if(r.status===403) setErr(t.accountSuspendedOwner||"Account suspended.");
        else setErr(isRTL?"بيانات غير صحيحة":d.error||"Incorrect email or password.");
        setSubmitting(false);return;
      }
      /* Find the workspace object from the returned ws_key */
      if(d.token)setAuthToken(d.token);
      const ws=workspaces.find(w=>w.wsKey===d.ws_key&&w.status==="active");
      if(!ws){setErr("Workspace not found or inactive.");setSubmitting(false);return;}
      setSubmitting(false);
      onLogin(ws,d.user);
    }catch{
      setErr(isRTL?"خطأ في الشبكة — يرجى المحاولة مرة أخرى.":"Network error — please try again.");
      setSubmitting(false);
    }
  };

  /* ══ MOBILE LOGIN ══ */
  if(isMobileLogin){
    const mInp=tc(theme,"p-inp-dark");
    return(
      <div className={`cmd-landing ${theme==="dark"?"p-dark":"p-light"}${isRTL?" rtl":""} pml-wrap`}>
        {vt.Toast}
        <style>{`\nbody{background:${theme==="dark"?"#08080f":"#FAFAF9"};overflow:hidden;margin:0;}`}</style>
        {mlAccentCss&&<style>{mlAccentCss}</style>}
        <div className={tc(theme,"p-dark-blob")}/>
        <div className="p-hero-grid" style={{position:"fixed"}}/>
        <div className="pml-slide" style={{flex:1,minWidth:"auto",width:"auto"}}>
          <div className="pml-glass" style={{minHeight:420,display:"flex",flexDirection:"column",justifyContent:"center"}}>
            {/* Logo — iridescent gradient tile */}
            <div style={{display:"flex",justifyContent:"center",marginBottom:20}}>
              <div style={{width:72,height:72,borderRadius:16,background:"var(--accent-grad)",display:"grid",placeItems:"center",overflow:"hidden",boxShadow:"0 8px 24px -6px color-mix(in srgb, var(--accent-a) 50%, transparent)"}}>
                <img src={platform.platformLogo||"/icon.svg"} style={{width:"100%",height:"100%",objectFit:"contain",filter:"brightness(0) invert(1)"}} alt={platform.platformName||"Command"}/>
              </div>
            </div>

            {/* ── Sign-in view (hidden when forgot password is open) ── */}
            {!showForgot&&(<>
              <h2 style={{fontFamily:mlFont,fontSize:24,fontWeight:700,color:mlInk,marginBottom:4,textAlign:"center"}}>{t.signIn}</h2>
              <p style={{fontSize:13,color:mlMuted,marginBottom:20,textAlign:"center"}}>{platform.platformName}</p>

              {err&&<div style={{background:"rgba(220,50,50,.1)",border:"1px solid rgba(220,50,50,.2)",borderRadius:10,padding:"10px 12px",fontSize:12,color:"#ff7070",marginBottom:12}}>{err}</div>}

              <div style={{marginBottom:10}}>
                <label style={{fontSize:10,fontWeight:600,color:mlMuted,marginBottom:4,display:"block",textTransform:"uppercase",letterSpacing:".06em"}}>{t.email}</label>
                <input className={mInp} type="email" value={email} onChange={e=>setEmail(e.target.value)} autoFocus onKeyDown={e=>e.key==="Enter"&&submit()} style={{fontSize:16}}/>
              </div>
              <div style={{marginBottom:6}}>
                <label style={{fontSize:10,fontWeight:600,color:mlMuted,marginBottom:4,display:"block",textTransform:"uppercase",letterSpacing:".06em"}}>{t.password}</label>
                <div style={{position:"relative"}}>
                  <input className={mInp} type={showLoginPw?"text":"password"} value={pw} onChange={e=>setPw(e.target.value)} placeholder="••••••••" onKeyDown={e=>e.key==="Enter"&&submit()} style={{fontSize:16,paddingInlineEnd:44}}/>
                  <button onClick={()=>setShowLoginPw(p=>!p)} style={{position:"absolute",insetInlineEnd:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",padding:4,color:mlMuted}}>
                    {showLoginPw?<svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z"/><circle cx="10" cy="10" r="3"/></svg>
                    :<svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z"/><circle cx="10" cy="10" r="3"/><line x1="3" y1="3" x2="17" y2="17"/></svg>}
                  </button>
                </div>
              </div>
              <div style={{display:"flex",justifyContent:"flex-end",marginBottom:14}}>
                <button onClick={()=>{setShowForgot(true);setFpStep(null);setFpCode("");setFpNewPw("");setFpErr("");}} style={{background:"none",border:"none",fontSize:11,color:mlAcc,cursor:"pointer",padding:0}}>{isRTL?"نسيت كلمة المرور؟":"Forgot password?"}</button>
              </div>

              <button className="pml-btn" onClick={submit} disabled={submitting}
                style={{background:"var(--accent-grad)",color:"#fff",border:"none",borderRadius:14,fontSize:16,fontWeight:600,padding:"15px 24px",cursor:"pointer",width:"100%",boxShadow:"0 6px 22px -4px color-mix(in srgb, var(--accent-a) 50%, transparent)",opacity:submitting?.6:1}}>
                {submitting?(isRTL?"جاري الدخول...":"Signing in..."):t.signIn}
              </button>

              <div style={{marginTop:14,textAlign:"center",fontSize:12,color:mlMuted}}>
                {t.noWs}<span style={{color:mlAcc,cursor:"pointer",fontWeight:600}} onClick={onBack}>{isRTL?"أنشئ واحدة":"Create one"}</span>
              </div>

              <button onClick={onBack} style={{background:"none",border:"none",color:mlSubtle,fontSize:12,cursor:"pointer",padding:"12px 0",width:"100%",textAlign:"center",marginTop:4}}>{isRTL?"رجوع":"Back"}</button>
            </>)}

            {/* ── Forgot password view (replaces sign-in) ── */}
            {showForgot&&(
              <div>
                <h2 style={{fontFamily:mlFont,fontSize:22,fontWeight:700,color:mlInk,marginBottom:4,textAlign:"center"}}>{isRTL?"إعادة تعيين كلمة المرور":"Reset password"}</h2>
                <p style={{fontSize:13,color:mlMuted,marginBottom:16,textAlign:"center"}}>{platform.platformName}</p>

                {fpErr&&<div style={{background:"rgba(220,50,50,.1)",border:"1px solid rgba(220,50,50,.2)",borderRadius:10,padding:"10px 12px",fontSize:12,color:"#ff7070",marginBottom:12}}>{fpErr}</div>}

                {/* Step 1: Enter email + send code */}
                {!fpStep&&(<>
                  <div style={{marginBottom:10}}>
                    <label style={{fontSize:10,fontWeight:600,color:mlMuted,marginBottom:4,display:"block",textTransform:"uppercase",letterSpacing:".06em"}}>{t.email}</label>
                    <input className={mInp} type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder={isRTL?"أدخل بريدك":"Enter your email"} style={{fontSize:16}} autoFocus/>
                  </div>
                  <div style={{fontSize:11,color:mlMuted,marginBottom:12}}>{isRTL?"سنرسل رمز تحقق إلى بريدك":"We'll send a verification code to your email."}</div>
                  <button onClick={async()=>{
                    if(!email.trim()){setFpErr(isRTL?"أدخل بريدك":"Enter your email.");return;}
                    setFpErr("");setFpSending(true);
                    try{const r=await authFetch("/api/send-verification",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:email.trim(),purpose:"password_reset"})});const d=await r.json();if(!d.ok){setFpErr(d.error||(isRTL?"فشل":"Failed"));setFpSending(false);return;}setFpStep("code");setFpSending(false);}catch{setFpErr(isRTL?"خطأ في الشبكة.":"Network error.");setFpSending(false);}
                  }} disabled={fpSending}
                    className="pml-btn" style={{background:"var(--accent-grad)",color:"#fff",border:"none",borderRadius:14,fontSize:15,fontWeight:600,padding:"14px 24px",cursor:"pointer",width:"100%",boxShadow:"0 6px 22px -4px color-mix(in srgb, var(--accent-a) 50%, transparent)",opacity:fpSending?.6:1}}>
                    {fpSending?(isRTL?"جاري الإرسال...":"Sending..."):(isRTL?"إرسال الرمز":"Send code")}
                  </button>
                </>)}

                {/* Step 2: Enter code */}
                {fpStep==="code"&&(<>
                  <div style={{fontSize:12,color:mlMuted,marginBottom:10,textAlign:"center"}}>{isRTL?"أدخل الرمز المرسل إلى":"Code sent to"} <span style={{color:mlInk,fontWeight:600}}>{email}</span></div>
                  <input className={mInp} value={fpCode} onChange={e=>setFpCode(e.target.value.replace(/\D/g,""))} maxLength={6} placeholder={isRTL?"رمز التحقق":"6-digit code"} style={{fontSize:18,textAlign:"center",letterSpacing:8,fontFamily:"'JetBrains Mono',monospace",marginBottom:10}} autoFocus/>
                  <button onClick={()=>{
                    if(fpCode.length<4){setFpErr(isRTL?"أدخل الرمز":"Enter the code.");return;}
                    setFpErr("");setFpStep("newpw");
                  }}
                    className="pml-btn" style={{background:"var(--accent-grad)",color:"#fff",border:"none",borderRadius:14,fontSize:15,fontWeight:600,padding:"14px 24px",cursor:"pointer",width:"100%",boxShadow:"0 6px 22px -4px color-mix(in srgb, var(--accent-a) 50%, transparent)"}}>
                    {isRTL?"متابعة":"Continue"}
                  </button>
                </>)}

                {/* Step 3: New password + confirm + eye toggle */}
                {fpStep==="newpw"&&(<>
                  <div style={{fontSize:12,color:mlMuted,marginBottom:10,textAlign:"center"}}>{isRTL?"أدخل كلمة المرور الجديدة":"Choose a new password"}</div>
                  <div style={{marginBottom:8,position:"relative"}}>
                    <input className={mInp} type={fpShowPw?"text":"password"} value={fpNewPw} onChange={e=>setFpNewPw(e.target.value)} placeholder={isRTL?"كلمة المرور الجديدة":"New password"} style={{fontSize:16,paddingInlineEnd:44}} autoFocus/>
                    <button onClick={()=>setFpShowPw(p=>!p)} style={{position:"absolute",insetInlineEnd:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",padding:4,color:mlMuted,fontSize:14}}>
                      {fpShowPw
                        ?<svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z"/><circle cx="10" cy="10" r="3"/></svg>
                        :<svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z"/><circle cx="10" cy="10" r="3"/><line x1="3" y1="3" x2="17" y2="17"/></svg>}
                    </button>
                  </div>
                  <div style={{marginBottom:10,position:"relative"}}>
                    <input className={mInp} type={fpShowPw?"text":"password"} value={fpConfirmPw} onChange={e=>setFpConfirmPw(e.target.value)} placeholder={isRTL?"تأكيد كلمة المرور":"Confirm password"} style={{fontSize:16,paddingInlineEnd:44}}/>
                    <button onClick={()=>setFpShowPw(p=>!p)} style={{position:"absolute",insetInlineEnd:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",padding:4,color:mlMuted}}>
                      {fpShowPw?<svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z"/><circle cx="10" cy="10" r="3"/></svg>
                      :<svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z"/><circle cx="10" cy="10" r="3"/><line x1="3" y1="3" x2="17" y2="17"/></svg>}
                    </button>
                  </div>
                  <button onClick={async()=>{
                    const pwErr=validatePasswordClient(fpNewPw);
                    if(pwErr){setFpErr(pwErr);return;}
                    if(fpNewPw!==fpConfirmPw){setFpErr(isRTL?"كلمات المرور غير متطابقة":"Passwords do not match.");return;}
                    setFpErr("");setFpSending(true);
                    try{const r=await authFetch("/api/auth",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({action:"reset_password",email:email.trim(),code:fpCode.trim(),newPassword:fpNewPw})});const d=await r.json();if(!d.ok){setFpErr(d.error||(isRTL?"فشل التحديث":"Reset failed."));setFpSending(false);return;}setFpStep("done");setFpSending(false);setPw("");}catch{setFpErr(isRTL?"خطأ في الشبكة.":"Network error.");setFpSending(false);}
                  }} disabled={fpSending}
                    className="pml-btn" style={{background:"var(--accent-grad)",color:"#fff",border:"none",borderRadius:14,fontSize:15,fontWeight:600,padding:"14px 24px",cursor:"pointer",width:"100%",boxShadow:"0 6px 22px -4px color-mix(in srgb, var(--accent-a) 50%, transparent)",opacity:fpSending?.6:1}}>
                    {fpSending?(isRTL?"جاري التحديث...":"Updating..."):(isRTL?"تحديث كلمة المرور":"Update password")}
                  </button>
                </>)}

                {/* Done */}
                {fpStep==="done"&&(
                  <div style={{textAlign:"center",padding:"8px 0"}}>
                    <div style={{fontSize:15,color:"#2D7D52",fontWeight:600,marginBottom:8}}>✓ {isRTL?"تم تغيير كلمة المرور":"Password updated"}</div>
                    <div style={{fontSize:13,color:mlMuted,marginBottom:14}}>{isRTL?"يمكنك الآن تسجيل الدخول":"You can now sign in with your new password."}</div>
                    <button onClick={()=>{setShowForgot(false);setFpStep(null);setFpCode("");setFpNewPw("");setFpConfirmPw("");setFpShowPw(false);setFpErr("");}}
                      className="pml-btn" style={{background:"var(--accent-grad)",color:"#fff",border:"none",borderRadius:14,fontSize:15,fontWeight:600,padding:"14px 24px",cursor:"pointer",width:"100%",boxShadow:"0 6px 22px -4px color-mix(in srgb, var(--accent-a) 50%, transparent)"}}>{isRTL?"تسجيل الدخول":"Sign in"}</button>
                  </div>
                )}

                {/* Back to sign in */}
                {fpStep!=="done"&&(
                  <button onClick={()=>{setShowForgot(false);setFpStep(null);setFpCode("");setFpNewPw("");setFpConfirmPw("");setFpShowPw(false);setFpErr("");}}
                    style={{background:"none",border:"none",color:mlSubtle,fontSize:12,cursor:"pointer",padding:"12px 0",width:"100%",textAlign:"center",marginTop:4}}>{isRTL?"رجوع لتسجيل الدخول":"Back to sign in"}</button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if(theme==="dark"){
    const accentCss=buildDarkAccentCss(accent);
    return(
      <div className={`cmd-landing p-dark${isRTL?" rtl":""}`} style={{fontFamily:isRTL?"'Tajawal',-apple-system,sans-serif":"-apple-system,BlinkMacSystemFont,'SF Pro Display','SF Pro Text','Helvetica Neue',Helvetica,Arial,sans-serif"}}>
        {vt.Toast}

        {accentCss&&<style>{accentCss}</style>}
        <div className="p-dark-blob"/>
        <div className="p-hero-grid"/>
        <CommandNav
          logoSrc={platform.platformLogo||"/icon.svg"}
          logoAlt={platform.platformName||"Command"}
          lang={lang}
          setLang={setLang}
          onLogoClick={onBack}
          onBack={onBack}
          backText={t.back}
          showNavLinks={false}
          showSignIn={false}
          showSignUp={false}
        />
        <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:"120px 24px 24px",position:"relative",zIndex:1}}>
          <div style={{maxWidth:440,width:"100%"}}>
            <div style={{marginBottom:28}}>
              <h2 style={{fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','SF Pro Text','Helvetica Neue',Helvetica,Arial,sans-serif",fontSize:28,fontWeight:600,color:"white",marginBottom:6,letterSpacing:"-.02em"}}>{t.signIn}</h2>
              <p style={{fontSize:14,color:"rgba(255,255,255,.42)"}}>{platform.platformName}</p>
            </div>
            {err&&<div className="p-err-dark" style={{marginBottom:14}}>{err}</div>}
            <div className="p-fg-dark"><label className="p-lbl-dark">{t.email}</label><input className="p-inp-dark" type="email" value={email} onChange={e=>setEmail(e.target.value)} autoFocus onKeyDown={e=>e.key==="Enter"&&submit()}/></div>
            <div className="p-fg-dark"><label className="p-lbl-dark">{t.password}</label><input className="p-inp-dark" type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder="••••••••" onKeyDown={e=>e.key==="Enter"&&submit()}/></div>
            <div style={{display:"flex",justifyContent:"flex-end",marginBottom:8}}>
              <button onClick={()=>setShowForgot(true)} style={{background:"none",border:"none",fontSize:12,color:accent,cursor:"pointer",padding:0}}>{isRTL?"نسيت كلمة المرور؟":"Forgot password?"}</button>
            </div>
            <button className="p-btn-primary-dark" onClick={submit} style={{width:"100%",padding:"14px",fontSize:15,borderRadius:12}}>{t.signIn}</button>
            <div style={{marginTop:16,textAlign:"center",fontSize:13,color:"rgba(255,255,255,.42)"}}>{t.noWs}<span style={{color:accent,cursor:"pointer",fontWeight:500}} onClick={onBack}>{t.createOne}</span></div>
            {showForgot&&(
              <div style={{marginTop:16,padding:14,borderRadius:10,background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)"}}>
                <div style={{fontSize:13,color:"white",marginBottom:6}}>{isRTL?"إعادة تعيين كلمة المرور":"Reset your password"}</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,.42)",marginBottom:8}}>{isRTL?"أدخل بريدك الإلكتروني":"Enter your email and we'll send a reset link."}</div>
                {forgotSent?<div style={{fontSize:12,color:"#2D7D52"}}>{isRTL?"تم الإرسال!":"Sent! Check your email."}</div>
                :<button onClick={async()=>{if(!email.trim())return;try{await authFetch("/api/send-verification",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:email.trim()})});setForgotSent(true);}catch{}}} className="p-btn-ghost-dark" style={{fontSize:12,padding:"6px 14px"}}>{isRTL?"إرسال":"Send reset link"}</button>}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  return(
    <div className={`cmd-landing p-light${isRTL?" rtl":""}`} style={{minHeight:"100vh",fontFamily:isRTL?"'Tajawal',-apple-system,sans-serif":"-apple-system,BlinkMacSystemFont,'SF Pro Display','SF Pro Text','Helvetica Neue',Helvetica,Arial,sans-serif"}}>
      {vt.Toast}

      {(()=>{const a=buildLightAccentCss(accent);return a&&<style>{a}</style>;})()}
      <div className="p-light-blob"/>
      <div className="p-hero-grid"/>
      <CommandNav
        logoSrc={platform.platformLogo||"/icon.svg"}
        logoAlt={platform.platformName||"Command"}
        lang={lang}
        setLang={setLang}
        onLogoClick={onBack}
        onBack={onBack}
        backText={t.back}
        showNavLinks={false}
        showSignIn={false}
        showSignUp={false}
      />
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:"120px 24px 24px",position:"relative",zIndex:1}}>
        <div style={{maxWidth:440,width:"100%"}}>
          <div style={{marginBottom:28}}>
            <h2 style={{fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','SF Pro Text','Helvetica Neue',Helvetica,Arial,sans-serif",fontSize:28,fontWeight:600,color:"var(--cink,#1A1714)",marginBottom:6,letterSpacing:"-.02em"}}>{t.signIn}</h2>
            <p style={{fontSize:14,color:"var(--cg500,#6B6660)"}}>{platform.platformName}</p>
          </div>
          {err&&<div className="p-err-light" style={{marginBottom:14}}>{err}</div>}
          <div className="p-fg-light"><label className="p-lbl-light">{t.email}</label><input className="p-inp-light" type="email" value={email} onChange={e=>setEmail(e.target.value)} autoFocus onKeyDown={e=>e.key==="Enter"&&submit()}/></div>
          <div className="p-fg-light"><label className="p-lbl-light">{t.password}</label><input className="p-inp-light" type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder="••••••••" onKeyDown={e=>e.key==="Enter"&&submit()}/></div>
          <div style={{display:"flex",justifyContent:"flex-end",marginBottom:8}}>
            <button onClick={()=>setShowForgot(true)} style={{background:"none",border:"none",fontSize:12,color:accent,cursor:"pointer",padding:0}}>{isRTL?"نسيت كلمة المرور؟":"Forgot password?"}</button>
          </div>
          <button className="p-btn-primary-light" onClick={submit} style={{width:"100%",padding:"14px",fontSize:15,borderRadius:12}}>{t.signIn}</button>
          <div style={{marginTop:16,textAlign:"center",fontSize:13,color:"var(--cg400,#A09A94)"}}>{t.noWs}<span style={{color:accent,cursor:"pointer",fontWeight:500}} onClick={onBack}>{t.createOne}</span></div>
          {showForgot&&(
            <div style={{marginTop:16,padding:14,borderRadius:10,background:"var(--cg50,#F5F4F2)",border:"1px solid var(--cg200,#E8E5E0)"}}>
              <div style={{fontSize:13,color:"var(--cink,#1A1714)",marginBottom:6}}>{isRTL?"إعادة تعيين كلمة المرور":"Reset your password"}</div>
              <div style={{fontSize:12,color:"#6B6660",marginBottom:8}}>{isRTL?"أدخل بريدك الإلكتروني":"Enter your email and we'll send a reset link."}</div>
              {forgotSent?<div style={{fontSize:12,color:"#2D7D52"}}>{isRTL?"تم الإرسال!":"Sent! Check your email."}</div>
              :<button onClick={async()=>{if(!email.trim())return;try{await authFetch("/api/send-verification",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:email.trim()})});setForgotSent(true);}catch{}}} className="p-btn-ghost-light" style={{fontSize:12,padding:"6px 14px"}}>{isRTL?"إرسال":"Send reset link"}</button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
